import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { Meta } from '../api';
import { useExternalArt, useMeta } from '../meta';
import { usePrefs } from '../prefs';
import { navigate } from '../router';
import { TileFlags, TileScore } from './MediaFlags';
import { menuRef, type DisplayItem } from './Widgets';
import { useCardMenu, useHiddenCards } from './CardMenu';

/**
 * Netflix-weergave: een posterrij waarin de kaart waar je op staat openklapt tot een brede kaart
 * (achtergrond, clearlogo, voortgang, trailer zonder geluid). Onder de rij korte info over die titel.
 * Een billboard op dezelfde pagina volgt je selectie (via setBillboard).
 */

// ---------- Billboard volgt de selectie ----------

let billboardItem: DisplayItem | null = null;
const billboardListeners = new Set<(it: DisplayItem | null) => void>();
let billboardTimer: number | undefined;

export function setBillboard(it: DisplayItem | null, delay = 350) {
  window.clearTimeout(billboardTimer);
  billboardTimer = window.setTimeout(() => {
    billboardItem = it;
    billboardListeners.forEach((l) => l(it));
  }, delay);
}

export function useBillboardItem(): DisplayItem | null {
  const [it, setIt] = useState(billboardItem);
  useEffect(() => {
    billboardListeners.add(setIt);
    return () => {
      billboardListeners.delete(setIt);
      // Weg van de pagina: niets meer volgen
      if (!billboardListeners.size) billboardItem = null;
    };
  }, []);
  return it;
}

// ---------- Info-regel ----------

export function metaLine(it: DisplayItem, meta: Meta | null): string[] {
  const type = it.ref?.type === 'tv' || /serie/i.test(it.sub) ? 'Serie' : it.ref?.type === 'movie' ? 'Film' : null;
  const runtime = meta?.runtime ? (meta.runtime >= 60 ? `${Math.floor(meta.runtime / 60)}u ${meta.runtime % 60}m` : `${meta.runtime}m`) : null;
  const seasons = meta?.seasonCount ? `${meta.seasonCount} seizoen${meta.seasonCount === 1 ? '' : 'en'}` : null;
  return [type, ...(meta?.genres ?? []).slice(0, 2), meta?.year ? String(meta.year) : null, it.ref?.type === 'tv' ? seasons : runtime].filter((x): x is string => !!x);
}

export function InfoLine({ it, meta }: { it: DisplayItem; meta: Meta | null }) {
  const bits = metaLine(it, meta);
  const imdb = meta?.ratings?.imdb;
  return (
    <div className="nx-line">
      {bits.map((b, i) => <span key={i} className={i === 0 ? 'nx-strong' : undefined}>{b}</span>)}
      {meta?.certification && <span className="nx-cert">{meta.certification}</span>}
      {imdb ? <span className="nx-imdb"><b>IMDb</b>{imdb.toFixed(1).replace('.', ',')}</span> : null}
      {!it.missing && it.href ? <span className="nx-own">In bibliotheek</span> : null}
    </div>
  );
}

// ---------- Trailer ----------

export function TrailerFrame({ ytKey, sound, className }: { ytKey: string; sound: boolean; className: string }) {
  return (
    <div className={className} aria-hidden="true">
      <iframe
        key={`${ytKey}:${sound}`}
        src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(ytKey)}?autoplay=1&mute=${sound ? 0 : 1}&controls=0&disablekb=1&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&loop=1&playlist=${encodeURIComponent(ytKey)}&start=${sound ? 0 : 5}`}
        title="Trailer"
        tabIndex={-1}
        allow="autoplay; encrypted-media"
      />
    </div>
  );
}

const reduced = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const touch = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none)').matches;

// ---------- Rij ----------

export function NetflixRow({ heading, head, items, onOpen, moreTile, className = '' }: {
  heading: string; head: ReactElement; items: DisplayItem[]; onOpen: (it: DisplayItem) => void; moreTile: ReactElement | null; className?: string;
}) {
  const isHidden = useHiddenCards();
  items = items.filter((x) => !isHidden(x.plex?.ratingKey));
  const [active, setActive] = useState(0);
  const hoverTimer = useRef<number | undefined>(undefined);
  const prefs = usePrefs();
  const it = items[Math.min(active, items.length - 1)];
  const meta = useMeta(it?.ref ?? null);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const pick = (i: number, now = false) => {
    window.clearTimeout(hoverTimer.current);
    const go = () => {
      setActive(i);
      setBillboard(items[i]);
    };
    if (now) go();
    else hoverTimer.current = window.setTimeout(go, 180);
  };

  const open = (x: DisplayItem, i: number) => {
    // Aanraakscherm: eerste tik klapt open, tweede opent
    if (touch() && i !== active) {
      pick(i, true);
      return;
    }
    if (x.missing || !x.href) onOpen(x);
    else navigate(x.href);
  };

  if (!it) return null;
  return (
    <section className={`row nx-row ${className}`} aria-label={heading}>
      {head}
      <div className="row-track nx-track" data-focus-group onMouseLeave={() => window.clearTimeout(hoverTimer.current)}>
        {items.map((x, i) => (
          <NetflixCard
            key={x.key}
            it={x}
            active={i === active}
            trailers={prefs.cardTrailers && !reduced()}
            sound={prefs.trailerSound}
            onFocus={() => pick(i, true)}
            onHover={() => pick(i)}
            onOpen={() => open(x, i)}
          />
        ))}
        {moreTile}
      </div>
      {prefs.rowInfo && (
        <div className="nx-info" key={it.key} aria-live="polite">
          <InfoLine it={it} meta={meta} />
          {(meta?.overview ?? it.overview) && <p className="nx-overview">{meta?.overview ?? it.overview}</p>}
        </div>
      )}
    </section>
  );
}

function NetflixCard({ it, active, trailers, sound, onFocus, onHover, onOpen }: {
  it: DisplayItem; active: boolean; trailers: boolean; sound: boolean; onFocus: () => void; onHover: () => void; onOpen: () => void;
}) {
  const ext = useExternalArt(it.key.startsWith('plex:') ? it.ref : null);
  const meta = useMeta(active ? it.ref : null);
  const [playing, setPlaying] = useState(false);
  const logos = document.documentElement.dataset.logos !== 'off';
  const ytKey = trailers && active ? meta?.trailer?.key : undefined;

  useEffect(() => {
    setPlaying(false);
    if (!ytKey) return;
    const t = window.setTimeout(() => setPlaying(true), 1500);
    return () => window.clearTimeout(t);
  }, [ytKey]);

  const poster = ext?.poster ?? it.poster;
  const back = meta?.backdrop ?? ext?.backdrop ?? it.backdrop ?? poster;
  const logo = logos ? meta?.logo : null;
  const label = `${it.title}${it.sub ? `, ${it.sub}` : ''}${it.missing ? ', aanvragen' : ''}`;
  const menu = useCardMenu(menuRef(it), it.title);
  return (
    <button
      type="button"
      className={`card nx-card${active ? ' is-open' : ''}`}
      onClick={onOpen}
      onFocus={onFocus}
      onMouseEnter={onHover}
      aria-label={label}
      aria-expanded={active}
      {...menu.props}
    >
      <span className="nx-poster" style={poster ? { backgroundImage: `url("${poster}")` } : undefined}>
        {!poster && <span className="card-fallback">{it.title}</span>}
        {it.flags && <TileFlags flags={it.flags} />}
        <TileScore score={it.score} />
      </span>
      <span className="nx-wide" style={back ? { backgroundImage: `url("${back}")` } : undefined} aria-hidden={!active}>
        {playing && ytKey && <TrailerFrame ytKey={ytKey} sound={sound} className="nx-trailer" />}
        <span className="nx-shade" />
        {logo ? <img className="nx-logo" src={logo} alt="" /> : <span className="nx-title">{it.title}</span>}
        {it.flags && <TileFlags flags={it.flags} />}
        {playing && ytKey && <span className="nx-playing">▶ Trailer{sound ? '' : ' · geluid uit'}</span>}
        {it.badge && <span className={`lib-badge ${it.badgeTone ?? 'req'}`}>{it.badge}</span>}
        {it.progress !== undefined && <span className="progress"><span style={{ width: `${Math.round(it.progress * 100)}%` }} /></span>}
        {menu.dots}
      </span>
    </button>
  );
}
